// Env-aware client for MANUAL equity (Alpaca) trading — account, positions,
// place market order, close position. Paper routes to /api/PaperTrading, live to
// /api/EquityLiveTrading (real money). Both are broker-direct (no managed
// StrategyTrade) and gated server-side by the master + per-user kill switch.
//
// NOTE: distinct from equityLiveTradingService.ts, which is the *settings* client
// (live creds + kill switch) used by the Configuration page. THIS service is the
// *trading* client (account/positions/orders) used by the manual order panel.
// Paper's /positions returns a richer PositionDto shape (qty/avgEntryPrice/
// unrealizedPL); we normalize it to EquityPosition below.

import { API_BASE } from '@/env';
import { apiFetch } from '@/auth/apiClient';

export type EquityEnv = 'paper' | 'live';

export interface EquityAccount {
  accountNumber: string;
  status: string;
  equity: number | null;
  cash: number | null;
  buyingPower: number | null;
}

export interface EquityPosition {
  symbol: string;
  quantity: number;
  side: string;
  averageEntryPrice: number;
  marketValue: number;
  unrealizedProfitLoss: number;
  currentPrice: number;
  /** Paper only: set when the row is an AUTOMATION position (a managed StrategyTrade).
   * Those must close through the by-trade path — the symbol-close endpoint only knows
   * the manual sim ledger and could flatten an unrelated manual position instead. */
  tradeId: string | null;
}

export interface PlaceEquityOrder {
  symbol: string;
  side: 'buy' | 'sell';
  quantity: number;
}

// Paper exposes a richer account/positions shape on /api/PaperTrading; we read
// only the common fields. Live uses /api/EquityLiveTrading (the Phase-2 surface).
const base = (env: EquityEnv) => (env === 'live' ? '/api/EquityLiveTrading' : '/api/PaperTrading');

// Unwrap the API's {error} shape so users see "no live equity credentials
// configured", never a raw JSON blob (go-live audit P1).
async function readError(res: Response): Promise<string> {
  const text = await res.text();
  try {
    const body = JSON.parse(text);
    if (body?.error) return String(body.error);
  } catch { /* not JSON */ }
  return text || `Request failed (${res.status})`;
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await apiFetch(`${API_BASE}${path}`, init);
  if (!res.ok) throw new Error(await readError(res));
  // Some endpoints return no body (204); guard JSON parse.
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

// Paper's /positions returns PositionDto (qty/avgEntryPrice/unrealizedPL/…);
// live returns EquityPositionDto (quantity/averageEntryPrice/unrealizedProfitLoss).
// Normalize both to EquityPosition so the panel renders identically.
function normalizePositions(env: EquityEnv, raw: any[]): EquityPosition[] {
  if (!Array.isArray(raw)) return [];
  if (env === 'live') return raw.map((p) => ({ ...p, tradeId: null })) as EquityPosition[];
  return raw.map((p) => ({
    symbol: p.symbol,
    quantity: p.qty ?? p.quantity ?? 0,
    side: p.side ?? '',
    averageEntryPrice: p.avgEntryPrice ?? p.averageEntryPrice ?? 0,
    marketValue: p.marketValue ?? 0,
    unrealizedProfitLoss: p.unrealizedPL ?? p.unrealizedProfitLoss ?? 0,
    currentPrice: p.currentPrice ?? 0,
    // Kept, not dropped: routes automation rows to the by-trade close.
    tradeId: p.tradeId ?? null,
  }));
}

export function createEquityTradingService(env: EquityEnv) {
  const b = base(env);
  return {
    env,
    getAccount: () => req<EquityAccount>(`${b}/account`),
    getPositions: async () => normalizePositions(env, await req<any[]>(`${b}/positions`)),
    placeOrder: (o: PlaceEquityOrder) =>
      req<{ orderId: string; symbol: string; side: string; quantity: number }>(`${b}/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(o),
      }),
    // An automation row (paper, tradeId set) must close by TRADE: symbol-close only
    // knows the manual sim ledger, so with both ledgers holding the symbol it would
    // flatten the manual position while leaving the automation trade open.
    closePosition: (symbol: string, tradeId?: string | null) =>
      env === 'paper' && tradeId
        ? req<unknown>(`/api/PaperTrading/trades/${encodeURIComponent(tradeId)}/close`, { method: 'POST' })
        : req<unknown>(`${b}/positions/${encodeURIComponent(symbol)}/close`, { method: 'POST' }),
  };
}

export type EquityTradingService = ReturnType<typeof createEquityTradingService>;
